/**
 * ReconciliationFlag — behavioral port of the @smartbox/ui web component.
 *
 * Surfaces a visual warning when a file or extracted record needs attention:
 *   - Overall file confidence < 0.70 (the SmartBox confidenceThreshold).
 *   - Any individual ExtractedRecord confidence < 0.70.
 *
 * NOTE: BatchResult / FileResult do not currently carry reconciliation delta
 * fields (e.g. prior-value vs extracted-value diffs). This component bases
 * flagging solely on confidence thresholds. Richer reconciliation flags
 * (showing what changed vs what was expected) are available when the backend
 * exposes them in the FileResult shape — this is the extension point.
 *
 * NEVER imports @smartbox/ui (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. Uses inProgress (amber/warning) Badge tone
 * for reconciliation warnings — the closest semantic match to "needs review".
 */

import { View, StyleSheet } from 'react-native';
import type { ExtractedRecord } from '@beam/api-types';
import { Badge, Text } from '../ui';
import { theme } from '../../theme';

/** Confidence below this threshold triggers a flag (matches SmartBox config). */
const CONFIDENCE_THRESHOLD = 0.70;

interface Props {
  /** Overall file-level confidence (from FileResult.confidence). */
  fileConfidence: number;
  /** All extracted records for this file (to find low-confidence fields). */
  records: ExtractedRecord[];
}

export function ReconciliationFlag({ fileConfidence, records }: Props) {
  const lowConfidenceRecords = records.filter(
    (r) => r.confidence < CONFIDENCE_THRESHOLD,
  );

  const fileIsLow = fileConfidence < CONFIDENCE_THRESHOLD;
  const hasFlags = fileIsLow || lowConfidenceRecords.length > 0;

  if (!hasFlags) return null;

  return (
    <View style={styles.container} accessibilityRole="alert">
      <View style={styles.badgeRow}>
        <Badge status="inProgress" label="Needs Review" />
      </View>
      {fileIsLow && (
        <Text variant="small" style={{ color: theme.status.inProgress.fg }}>
          Low overall confidence ({(fileConfidence * 100).toFixed(0)}%) — review
          all fields carefully before confirming.
        </Text>
      )}
      {lowConfidenceRecords.length > 0 && (
        <Text variant="small" style={{ color: theme.status.inProgress.fg }}>
          {lowConfidenceRecords.length}{' '}
          {lowConfidenceRecords.length === 1 ? 'field' : 'fields'} below the
          confidence threshold — highlighted below.
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.status.inProgress.bg,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.status.inProgress.fg,
  },
  badgeRow: {
    flexDirection: 'row',
  },
});
